/**
 * Calibration View
 * Step-by-step calibration wizard for scale positions
 */

import { useState, useEffect } from 'react'
import { useMScaleStore } from '../store'
import { useBluetooth } from '../hooks/useBluetooth'
import { Card } from '../components/Common/Card'
import { Button } from '../components/Common/Button'
import { formatWeight } from '../utils/format'

type CalibrationStep = 'select' | 'zero' | 'span' | 'complete'

interface CalibrationViewProps {
  onBack?: () => void
}

const CALIBRATION_WEIGHT_KEY = 'mscale_last_calibration_weight'
const DEFAULT_CALIBRATION_WEIGHT = '10'

export function CalibrationView({ onBack }: CalibrationViewProps = {}) {
  const { weights, hardwareConfig, showHundredthsUnder10g } = useMScaleStore()
  const { startCalibration, completeZero, startSpan, completeSpan, cancelCalibration, setLEDWhite } = useBluetooth()

  const [selectedPosition, setSelectedPosition] = useState<number | null>(null)
  const [currentStep, setCurrentStep] = useState<CalibrationStep>('select')
  const [knownWeight, setKnownWeight] = useState<string>(() => {
    // Load last used weight from localStorage, or use default
    const saved = localStorage.getItem(CALIBRATION_WEIGHT_KEY)
    return saved || DEFAULT_CALIBRATION_WEIGHT
  })
  const [isProcessing, setIsProcessing] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Save known weight to localStorage whenever it changes
  useEffect(() => {
    const weight = parseFloat(knownWeight)
    if (!isNaN(weight) && weight > 0) {
      localStorage.setItem(CALIBRATION_WEIGHT_KEY, knownWeight)
    }
  }, [knownWeight])

  const gridCols = hardwareConfig?.gridCols ?? 4
  const activePositions = hardwareConfig?.activePositions ?? Array.from({ length: 16 }, (_, i) => i)

  const handleSelectPosition = async (position: number) => {
    setSelectedPosition(position)
    setCurrentStep('zero')
    setError(null)
    // Turn on white LED for selected position (10 minutes = plenty of time for calibration)
    try {
      await setLEDWhite(position, 600000) // 10 minutes
    } catch (err) {
      console.error('Failed to set LED:', err)
    }
  }

  const handleStartZero = async () => {
    if (selectedPosition === null) return

    setIsProcessing(true)
    setError(null)
    try {
      await startCalibration(selectedPosition)
      // Wait a moment for scale to settle
      await new Promise(resolve => setTimeout(resolve, 1000))
      await completeZero(selectedPosition)
      // Wait for device to complete zero operation (~500ms for 4 ADC readings at 10 SPS)
      await new Promise(resolve => setTimeout(resolve, 500))
      setCurrentStep('span')
    } catch (err) {
      setError('Failed to zero calibration. Please try again.')
      console.error(err)
    } finally {
      setIsProcessing(false)
    }
  }

  const handleStartSpan = async () => {
    if (selectedPosition === null) return

    const weight = parseFloat(knownWeight)
    if (isNaN(weight) || weight <= 0) {
      setError('Please enter a valid weight value')
      return
    }

    setIsProcessing(true)
    setError(null)
    try {
      await startSpan(selectedPosition, weight)
      // Wait for scale to settle and measure
      await new Promise(resolve => setTimeout(resolve, 2000))
      await completeSpan(selectedPosition)
      setCurrentStep('complete')
    } catch (err) {
      setError('Failed to complete span calibration. Please try again.')
      console.error(err)
    } finally {
      setIsProcessing(false)
    }
  }

  const handleReset = async () => {
    // If we're in an active calibration step, notify firmware to cancel
    if (currentStep !== 'select' && selectedPosition !== null) {
      await cancelCalibration(selectedPosition)
    }
    setSelectedPosition(null)
    setCurrentStep('select')
    // Don't reset knownWeight - keep the last used value
    setError(null)
  }

  const getPositionLabel = (position: number) => {
    const row = Math.floor(position / gridCols)
    const col = position % gridCols
    return `Position ${position} (Row ${row}, Col ${col})`
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <Card title="Scale Calibration">
        <div className="space-y-6">
          {/* Progress Indicator */}
          <div className="flex items-center justify-center gap-4">
            <div className={`flex items-center gap-2 ${currentStep === 'select' ? 'text-blue-600 font-semibold' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                currentStep === 'select' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'
              }`}>
                1
              </div>
              <span>Select Position</span>
            </div>
            <div className="w-12 h-0.5 bg-gray-300" />
            <div className={`flex items-center gap-2 ${currentStep === 'zero' ? 'text-blue-600 font-semibold' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                currentStep === 'zero' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'
              }`}>
                2
              </div>
              <span>Zero</span>
            </div>
            <div className="w-12 h-0.5 bg-gray-300" />
            <div className={`flex items-center gap-2 ${currentStep === 'span' ? 'text-blue-600 font-semibold' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                currentStep === 'span' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'
              }`}>
                3
              </div>
              <span>Calibrate</span>
            </div>
            <div className="w-12 h-0.5 bg-gray-300" />
            <div className={`flex items-center gap-2 ${currentStep === 'complete' ? 'text-green-600 font-semibold' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                currentStep === 'complete' ? 'bg-green-600 text-white' : 'bg-gray-200 text-gray-600'
              }`}>
                ✓
              </div>
              <span>Complete</span>
            </div>
          </div>

          {error && (
            <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-sm text-red-800 dark:text-red-300">{error}</p>
            </div>
          )}

          {/* Step Content */}
          {currentStep === 'select' && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Select a Scale Position to Calibrate
              </h3>
              <div className="grid grid-cols-4 gap-3">
                {Array.from({ length: 16 }, (_, i) => i).map(position => {
                  const isActive = activePositions.includes(position)
                  const weight = weights[position] ?? 0

                  return (
                    <button
                      key={position}
                      onClick={() => isActive && handleSelectPosition(position)}
                      onMouseEnter={() => isActive && setLEDWhite(position, 500)}
                      disabled={!isActive}
                      className={`
                        p-4 rounded-lg border-2 transition-all
                        ${isActive
                          ? 'border-gray-300 hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 cursor-pointer'
                          : 'border-gray-200 bg-gray-100 dark:bg-gray-800 cursor-not-allowed opacity-50'
                        }
                      `}
                    >
                      <div className="text-sm font-semibold text-gray-900 dark:text-white">
                        Pos {position}
                      </div>
                      {isActive && (
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {formatWeight(weight, showHundredthsUnder10g)}g
                        </div>
                      )}
                    </button>
                  )
                })}
              </div>
            </div>
          )}

          {currentStep === 'zero' && selectedPosition !== null && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Zero Calibration
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                {getPositionLabel(selectedPosition)}
              </p>

              <div className="p-6 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800 mb-6">
                <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-200 mb-3">
                  Instructions:
                </h4>
                <ol className="text-sm text-blue-800 dark:text-blue-300 space-y-2 list-decimal list-inside">
                  <li>Remove all weight from the selected scale pad</li>
                  <li>Ensure the pad is completely empty and clean</li>
                  <li>Wait for the reading to stabilize</li>
                  <li>Click "Zero Scale" when ready</li>
                </ol>
              </div>

              <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 mb-6">
                <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                  Current Reading:
                </div>
                <div className="text-3xl font-mono font-bold text-gray-900 dark:text-white">
                  {formatWeight(weights[selectedPosition] ?? 0, showHundredthsUnder10g)}g
                </div>
              </div>

              <div className="flex gap-3">
                <Button
                  variant="secondary"
                  onClick={handleReset}
                  disabled={isProcessing}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={handleStartZero}
                  disabled={isProcessing}
                  className="flex-1"
                >
                  {isProcessing ? 'Zeroing...' : 'Zero Scale'}
                </Button>
              </div>
            </div>
          )}

          {currentStep === 'span' && selectedPosition !== null && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Span Calibration
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                {getPositionLabel(selectedPosition)}
              </p>

              <div className="p-6 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800 mb-6">
                <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-200 mb-3">
                  Instructions:
                </h4>
                <ol className="text-sm text-blue-800 dark:text-blue-300 space-y-2 list-decimal list-inside">
                  <li>Place a known weight on the scale pad</li>
                  <li>Enter the exact weight value below (in grams)</li>
                  <li>Wait for the reading to stabilize</li>
                  <li>Click "Calibrate" when ready</li>
                </ol>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Known Weight (grams)
                </label>
                <input
                  type="number"
                  value={knownWeight}
                  onChange={(e) => setKnownWeight(e.target.value)}
                  onFocus={(e) => e.target.select()}
                  step="0.1"
                  min="0.1"
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                  disabled={isProcessing}
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Recommended: Use a precision weight between 5-50g
                </p>
              </div>

              <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 mb-6">
                <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                  Current Reading:
                </div>
                <div className="text-3xl font-mono font-bold text-gray-900 dark:text-white">
                  {formatWeight(weights[selectedPosition] ?? 0, showHundredthsUnder10g)}g
                </div>
              </div>

              <div className="flex gap-3">
                <Button
                  variant="secondary"
                  onClick={handleReset}
                  disabled={isProcessing}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={handleStartSpan}
                  disabled={isProcessing}
                  className="flex-1"
                >
                  {isProcessing ? 'Calibrating...' : 'Calibrate'}
                </Button>
              </div>
            </div>
          )}

          {currentStep === 'complete' && selectedPosition !== null && (
            <div>
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                  Calibration Complete!
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                  {getPositionLabel(selectedPosition)} has been successfully calibrated.
                </p>

                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 mb-6 inline-block">
                  <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                    Final Reading:
                  </div>
                  <div className="text-3xl font-mono font-bold text-gray-900 dark:text-white">
                    {formatWeight(weights[selectedPosition] ?? 0, showHundredthsUnder10g)}g
                  </div>
                </div>
              </div>

              <div className="flex gap-3 justify-center">
                <Button
                  variant="secondary"
                  onClick={handleReset}
                >
                  Calibrate Another Position
                </Button>
                <Button
                  variant="primary"
                  onClick={() => onBack ? onBack() : window.history.back()}
                >
                  Done
                </Button>
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* Help Section */}
      <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
        <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
          Calibration Tips
        </h4>
        <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
          <li>• Calibrate in a stable environment without vibrations</li>
          <li>• Use precision calibration weights for best accuracy</li>
          <li>• Calibrate all positions for consistent performance</li>
          <li>• Re-calibrate if you notice drift or inaccurate readings</li>
          <li>• Recommended calibration weights: 10g, 20g, 30g, or 50g</li>
        </ul>
      </div>
    </div>
  )
}
